import { useEffect, useRef, useCallback, useMemo } from 'react';
import { useRealtimeStore, ChatMessage, ControlResult, ControlError } from '@/stores/realtimeStore';
import { getConfig } from '@/api/apiConfig';
import { calculateBackoffDelay } from '@/utils/retry';
import { hasValidAccessToken, hasStoredRefreshToken } from '@/stores/userStore';

// ── Capture SSE 이벤트 데이터 ──
export interface CaptureEventData {
  sessionId: number;
  groupId?: number;
  nodeId?: number;
  nodeName?: string;
  houseId?: number;
  houseName?: string;
  imageUrl?: string;
  capturedAt?: string;
  petName?: string;
  identifiedBy?: string;
}

// ── 옵션 ──
/**
 * 말하기(PTT) SSE 이벤트.
 * 계약: `docs/poc_customer/backend-req/PTT-SSE-연동-프론트엔드-작업서.md`
 */
export interface PttStartedEvent {
  nodeId: number;
  speakerName?: string;
  /**
   * 발언자 식별자. **본인 여부 판정에 쓴다** — 발언자 본인도 이 이벤트를 받으므로
   * 이 값으로 걸러야 자기 화면에 "○○님 말하는 중"이 뜨지 않는다.
   */
  speakerUserId?: string;
  /** **오프셋 없는 UTC**. 녹화 영상 API(+09:00)와 형식이 다르다. */
  expiresAt?: string;
}

/**
 * 기기가 구독을 시작했다 = "이제 말하세요".
 * 이 이벤트 전에는 말해도 전달되지 않는다 — SRS가 publisher보다 먼저 붙은 구독자에게
 * 미디어를 주지 않으므로, 서버는 브라우저 송출이 수락된 뒤에야 기기에 구독을 지시한다.
 */
export interface PttReadyEvent {
  nodeId: number;
  speakerName?: string;
}

export type PttEndedReason =
  /** 발언자가 직접 종료 */
  | 'user-release'
  /** 30초 상한 도달 · lock TTL 만료 */
  | 'expired'
  /** 브라우저 송출이 끊김 (탭 종료·네트워크) */
  | 'unpublish'
  /** 기기에 제어 전송 실패 — 본인에게만 안내 */
  | 'device-command-failed'
  /** kick 계열 등 서버 강제 종료 */
  | (string & {});

export interface PttEndedEvent {
  nodeId: number;
  reason?: PttEndedReason;
}

/**
 * 세 이벤트를 하나의 콜백으로 묶는다. capture.* 처럼 이벤트별로 구독을 나누지 않은 이유는
 * 셋이 하나의 상태 기계(대기 → 연결 중 → 말하는 중)를 이루기 때문이다 —
 * 나눠 구독하면 순서 보장과 등록 동기화를 소비자가 떠안게 된다.
 */
export type PttEvent =
  | { type: 'started'; data: PttStartedEvent }
  | { type: 'ready'; data: PttReadyEvent }
  | { type: 'ended'; data: PttEndedEvent };

export interface UseRealtimeConnectionOptions {
  enabled?: boolean;
  onChatMessage?: (msg: ChatMessage) => void;
  onControlResult?: (result: ControlResult) => void;
  onControlError?: (error: ControlError) => void;
  onCapturePending?: (data: CaptureEventData) => void;
  onCaptureIdentified?: (data: CaptureEventData) => void;
  onCaptureExpired?: (data: CaptureEventData) => void;
  onPttEvent?: (event: PttEvent) => void;
  onStatusChange?: (status: string) => void;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
}

// 30분 타임아웃 (서버 스펙)
const SSE_STREAM_TIMEOUT = 30 * 60 * 1000;

export function useRealtimeConnection({
  enabled = false,
  onChatMessage,
  onControlResult,
  onControlError,
  onCapturePending,
  onCaptureIdentified,
  onCaptureExpired,
  onPttEvent,
  onStatusChange,
  reconnectInterval = 3000,
  maxReconnectAttempts = 5,
}: UseRealtimeConnectionOptions) {
  const esRef = useRef<EventSource | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 최신 콜백을 ref로 유지 (의존성 변경에 따른 재연결 방지)
  const onChatMessageRef = useRef(onChatMessage);
  onChatMessageRef.current = onChatMessage;
  const onControlResultRef = useRef(onControlResult);
  onControlResultRef.current = onControlResult;
  const onControlErrorRef = useRef(onControlError);
  onControlErrorRef.current = onControlError;
  const onPttEventRef = useRef(onPttEvent);
  onPttEventRef.current = onPttEvent;
  const onCapturePendingRef = useRef(onCapturePending);
  onCapturePendingRef.current = onCapturePending;
  const onCaptureIdentifiedRef = useRef(onCaptureIdentified);
  onCaptureIdentifiedRef.current = onCaptureIdentified;
  const onCaptureExpiredRef = useRef(onCaptureExpired);
  onCaptureExpiredRef.current = onCaptureExpired;
  const onStatusRef = useRef(onStatusChange);
  onStatusRef.current = onStatusChange;

  const store = useMemo(() => {
    const s = useRealtimeStore.getState();
    return {
      setSseStatus: s.setSseStatus,
      setSseReconnectAttempts: s.setSseReconnectAttempts,
      setSseShowError: s.setSseShowError,
      setSseEventSource: s.setSseEventSource,
      addChatMessage: s.addChatMessage,
      updateCommandResult: s.updateCommandResult,
      reset: s.reset,
    };
  }, []);

  const baseUrl = getConfig().baseUrl;

  // ════════════════════════════════════════════════════════════════════════════
  // 통합 SSE 연결 — GET /api/v1/events?token=xxx
  // 이벤트 타입: system.connected, control.result, control.error,
  //             chat.message, chat.read, chat.notification, chat.system, chat.room_updated
  // ════════════════════════════════════════════════════════════════════════════
  const connect = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const url = `${baseUrl}/api/v1/events?token=${encodeURIComponent(token)}`;
    try {
      store.setSseStatus('connecting');
      const es = new EventSource(url);
      esRef.current = es;
      store.setSseEventSource(es);

      // ── system.connected ──
      es.addEventListener('system.connected', () => {
        store.setSseStatus('connected');
        store.setSseReconnectAttempts(0);
        store.setSseShowError(false);
        onStatusRef.current?.('connected');

        // 30분 타임아웃 재연결
        if (timeoutTimer.current) clearTimeout(timeoutTimer.current);
        timeoutTimer.current = setTimeout(() => {
          disconnect();
          connect();
        }, SSE_STREAM_TIMEOUT);
      });

      // ── control.result ──
      es.addEventListener('control.result', (event) => {
        try {
          const data = JSON.parse((event as MessageEvent).data) as ControlResult;
          store.updateCommandResult(data.commandId, data);
          onControlResultRef.current?.(data);
        } catch {
          // ignore parse errors
        }
      });

      // ── control.error ──
      es.addEventListener('control.error', (event) => {
        try {
          const data = JSON.parse((event as MessageEvent).data) as ControlError;
          if (data.commandId) {
            store.updateCommandResult(data.commandId, {
              commandId: data.commandId,
              nodeId: data.nodeId ?? 0,
              controlType: '',
              success: false,
              errorCode: data.errorCode,
              errorMessage: data.errorMessage,
            });
          }
          onControlErrorRef.current?.(data);
        } catch {
          // ignore parse errors
        }
      });

      // ── chat.message ──
      es.addEventListener('chat.message', (event) => {
        try {
          const data = JSON.parse((event as MessageEvent).data) as ChatMessage;
          store.addChatMessage(data);
          onChatMessageRef.current?.(data);
        } catch {
          // ignore parse errors
        }
      });

      // ── capture.pending — 새 미식별 개체 감지 ──
      es.addEventListener('capture.pending', (event) => {
        try {
          const data = JSON.parse((event as MessageEvent).data) as CaptureEventData;
          onCapturePendingRef.current?.(data);
        } catch {
          // ignore parse errors
        }
      });

      // ── capture.identified — 다른 매니저가 식별 완료 ──
      es.addEventListener('capture.identified', (event) => {
        try {
          const data = JSON.parse((event as MessageEvent).data) as CaptureEventData;
          onCaptureIdentifiedRef.current?.(data);
        } catch {
          // ignore parse errors
        }
      });

      // ── ptt.started / ptt.ready / ptt.ended ──
      // 발언 상태는 ptt.* 로만 판단한다. control.result의 ACTIVATE_PTT는 서버가 기기에
      // 보내는 내부 제어가 발언자 계정으로 기록돼 딸려오는 것이므로 무시해야 한다
      // (펌웨어 미지원 시 DEVICE_ERROR로 와서 토스트를 띄우면 오해를 부른다).
      const pttListeners: Array<[string, PttEvent['type']]> = [
        ['ptt.started', 'started'],
        ['ptt.ready', 'ready'],
        ['ptt.ended', 'ended'],
      ];
      pttListeners.forEach(([eventName, type]) => {
        es.addEventListener(eventName, (event) => {
          try {
            const data = JSON.parse((event as MessageEvent).data);
            onPttEventRef.current?.({ type, data } as PttEvent);
          } catch {
            // ignore parse errors
          }
        });
      });

      // ── onerror ──
      es.onerror = () => {
        store.setSseStatus('disconnected');
        onStatusRef.current?.('disconnected');
        es.close();
        esRef.current = null;
        store.setSseEventSource(null);

        if (timeoutTimer.current) {
          clearTimeout(timeoutTimer.current);
          timeoutTimer.current = null;
        }

        // 인증 종료 판별로만 재연결을 중단한다.
        // EventSource는 onerror에서 HTTP 상태코드를 노출하지 않으므로 readyState(CLOSED)로
        // 인증거부를 단정하면 서버 재시작·5xx·네트워크 블립에서도 영구 끊김이 발생한다(기존 버그).
        // 대신 토큰 상태로 판별: 토큰이 없거나(로그아웃) 만료됐고 리프레시 토큰도 없으면 중단.
        const hasToken = !!localStorage.getItem('token');
        if (!hasToken || (!hasValidAccessToken() && !hasStoredRefreshToken())) {
          store.setSseShowError(true);
          return;
        }

        // 그 외(서버 재시작·네트워크 단절·5xx 등)는 지수 백오프로 지속 재연결.
        // connect()가 매번 localStorage 토큰을 다시 읽으므로 리프레시 후 자동 복구된다.
        const attempts = useRealtimeStore.getState().sseReconnectAttempts;
        store.setSseReconnectAttempts(attempts + 1);
        if (attempts + 1 >= maxReconnectAttempts) {
          // 사용자에겐 에러를 표시하되 백그라운드 재시도는 계속(영구 포기 없음)
          store.setSseShowError(true);
        }
        const delay = calculateBackoffDelay(attempts, {
          baseDelay: reconnectInterval,
          maxDelay: 30_000,
          multiplier: 2,
          jitter: 0.2,
        });
        reconnectTimer.current = setTimeout(connect, delay);
      };
    } catch {
      store.setSseStatus('disconnected');
      store.setSseShowError(true);
    }
  }, [baseUrl, reconnectInterval, maxReconnectAttempts, store]);

  const disconnect = useCallback(() => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    if (timeoutTimer.current) {
      clearTimeout(timeoutTimer.current);
      timeoutTimer.current = null;
    }
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
      store.setSseEventSource(null);
    }
    store.setSseStatus('disconnected');
  }, [store]);

  const reconnect = useCallback(() => {
    disconnect();
    store.setSseReconnectAttempts(0);
    store.setSseShowError(false);
    connect();
  }, [connect, disconnect, store]);

  // ── 라이프사이클 ──
  useEffect(() => {
    if (enabled) connect();
    else disconnect();
    return () => disconnect();
  }, [enabled, connect, disconnect]);

  // ── 읽기 전용 상태 구독 ──
  const sseStatus = useRealtimeStore((s) => s.sseStatus);
  const reconnectAttempts = useRealtimeStore((s) => s.sseReconnectAttempts);
  const showError = useRealtimeStore((s) => s.sseShowError);
  const chatMessages = useRealtimeStore((s) => s.chatMessages);

  return {
    // 연결 상태 (채팅 useConnection 호환)
    isConnected: sseStatus === 'connected',
    connectionStatus: sseStatus,
    reconnectAttempts,
    showConnectionError: showError,
    messages: chatMessages,
    messageContext: null,
    allMessageContexts: [],
    connect,
    disconnect,
    reconnect,
  };
}
